/**
 * StateBadge — F-005 Phase 4 (IMPL-P4-001). Promoted from
 * `arness-sketches/pratica-status/StateBadge.tsx` (direct promotion).
 *
 * Maps each pratica state to a WCAG AA compliant colour pairing rendered as a
 * shadcn/ui outline Badge. The `PraticaStato` type comes from `@app/shared`
 * (the DB enum is the single source of truth) — the sketch's local union has
 * been dropped.
 *
 * NOTE: the enum keys are the DB values; user-facing labels can differ
 * (`IN_ELABORAZIONE` → "In lavorazione", `COMPLETATA` → "Registrata"). There is
 * no `REGISTRATA` / `ARCHIVIATA` / `IN_LAVORAZIONE` value in the DB enum.
 */
import type { PraticaStato } from '@app/shared';

import { Badge } from '@/components/ui/badge';

const STATO_CONFIG: Record<PraticaStato, { label: string; className: string }> = {
  BOZZA: { label: 'Bozza', className: 'bg-gray-100 text-gray-700 ring-gray-200' },
  INVIATA: { label: 'Inviata', className: 'bg-blue-50 text-blue-700 ring-blue-200' },
  IN_CARICO: { label: 'In carico', className: 'bg-indigo-50 text-indigo-700 ring-indigo-200' },
  IN_ELABORAZIONE: {
    label: 'In lavorazione',
    className: 'bg-indigo-100 text-indigo-800 ring-indigo-300',
  },
  BLOCCATA: { label: 'Bloccata', className: 'bg-amber-50 text-amber-700 ring-amber-200' },
  COMPLETATA: { label: 'Registrata', className: 'bg-green-50 text-green-700 ring-green-200' },
  ANNULLATA: { label: 'Annullata', className: 'bg-red-50 text-red-700 ring-red-200' },
};

interface StateBadgeProps {
  stato: PraticaStato;
  className?: string;
}

export function StateBadge({ stato, className }: StateBadgeProps) {
  const config = STATO_CONFIG[stato];
  return (
    <Badge
      variant="outline"
      className={[
        'rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset',
        config.className,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={`Stato pratica: ${config.label}`}
    >
      {config.label}
    </Badge>
  );
}
